import { CategoriaEnum } from '@acme/auth'
import { Autocomplete, Button, ButtonGroup, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useEffect, useMemo } from 'react'
import { funcionario as funcionario_api } from 'service/api'
import { useStore } from 'store'
import { filterLocaisCentro } from './filter-select'
import { SelectProps } from './types'

interface IProps extends SelectProps {
    centroCustoId?: number
    localId?: number
    children?: never
}

export function SelectFuncionario({
    size = 'small',
    width = 260,
    button: hasButtons = false,
    disabled,
    centroCustoId,
    localId,
    variant = 'filled',
}: IProps) {
    const {
        decoded: { categoria },
    } = useAuth()
    const diasRescisao = useStore(state => state.company?.diasRescisao)
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)
    const [competencia, competenciaAtual] = useStore(state => [
        state.competencia,
        state.competenciaAtual,
    ])

    const { data: funcionarios = [] } = funcionario_api.useFetch({
        queryParams: { empresaId },
        enabled: empresaId > 0,
    })

    const exibirApenasAtivos = categoria !== CategoriaEnum.Funcionario
    const data = React.useMemo(
        () =>
            funcionarios.filter(obj => {
                if (exibirApenasAtivos && obj.dtRescisao) {
                    const data =
                            competencia === competenciaAtual
                                ? dayjs()
                                : dayjs(competencia).startOf('month'),
                        tolerancia = diasRescisao ?? 40

                    if (
                        data
                            .add(tolerancia, 'day')
                            .isAfter(dayjs(obj.dtRescisao), 'day')
                    )
                        return false
                }

                return filterLocaisCentro<typeof obj>(
                    !!localId && localId > 0 ? [localId] : [],
                    [centroCustoId!],
                    it => it,
                )(obj)
            }),
        [
            funcionarios,
            exibirApenasAtivos,
            centroCustoId,
            localId,
            competencia,
            competenciaAtual,
            diasRescisao,
        ],
    )

    const isPrimeiroFuncionario = useMemo(() => {
        if (data && data.length > 0) {
            return funcionarioId === data[0].id
        }
        return false
    }, [data, funcionarioId])

    const isUltimoFuncionario = useMemo(() => {
        if (data && data.length > 0) {
            return funcionarioId === data[data.length - 1].id
        }
        return false
    }, [data, funcionarioId])

    async function next() {
        data.forEach((item, index) => {
            if (item.id === funcionarioId) {
                setEmployeeId(data.at(index + 1)?.id || -1)
            }
        })
    }

    async function previous() {
        data.forEach((item, index) => {
            if (item.id === funcionarioId) {
                setEmployeeId(data.at(index - 1)?.id || -1)
            }
        })
    }

    const selectedFuncionario = data.find(f => f.id === funcionarioId) || null

    // Set default employee if none selected or current not in list
    useEffect(() => {
        if (funcionarioId === -1 && data.length > 0) {
            setEmployeeId(data[0].id)
        } else if (
            funcionarioId !== -1 &&
            !data.find(f => f.id === funcionarioId)
        ) {
            setEmployeeId(data.at(0)?.id || -1)
        }
    }, [data, funcionarioId])

    return (
        <div className="flex items-center gap-2" key={selectedFuncionario?.id}>
            <Autocomplete
                className="flex-1"
                style={{ width }}
                options={data}
                getOptionLabel={option => option?.nome || ''}
                value={selectedFuncionario}
                onChange={(_, value) => {
                    if (value) setEmployeeId(value.id)
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={disabled}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Funcionário"
                        size={size}
                        variant={variant}
                        className="w-full truncate"
                        InputLabelProps={{ shrink: true }}
                        placeholder="Selecione um funcionário"
                    />
                )}
                noOptionsText={
                    <span className="text-gray-500 select-none">
                        Nenhum funcionário
                    </span>
                }
                clearIcon={null}
            />
            {hasButtons && (
                <ButtonGroup variant="outlined" className="h-full">
                    <Button
                        onClick={previous}
                        disabled={isPrimeiroFuncionario}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronLeftIcon />
                    </Button>
                    <Button
                        onClick={next}
                        disabled={isUltimoFuncionario}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronRightIcon />
                    </Button>
                </ButtonGroup>
            )}
        </div>
    )
}
